import React, { useState, useCallback, useEffect, useMemo } from 'react';
import Header from './Header';
import { feManagementSystemApi } from '../api/fe-management-system-api';
import type {
  GetNarrativeStatisticsResponse,
  TimelineDataPoint,
  NarrativeDetailDto,
  NarrativeStakeholderDto,
  NarrativeEventDto,
} from '../api/fe-management-system-api/narratives';
import { formatNumberToLargestUnit } from '../utils/formatters';
import getApiErrorMessage from '../utils/getApiErrorMessage';
import { useToasterStore } from '../stores/ui/toasterStore';
import { useUIStore } from '../stores/ui/uiStore';
import {
  NarrativeTotalMcapChart,
  NarrativeStackedMcapChart,
  NarrativeIndividualMcapChart,
  NarrativeTokenCountChart,
} from './NarrativeCharts';

const DAYS_OPTIONS = [7, 14, 30] as const;
const TOP_N_OPTIONS = [5, 10, 20] as const;

export interface NarrativeDetailPageProps {
  narrativeId: string;
  onBack: () => void;
  /** 삭제 성공 시 호출 (목록 최신화용) */
  onDeleted?: () => void;
}

/** 내러티브에 속한 토큰 목록: address + symbol (통계 마지막 시점 기준) */
function useNarrativeTokenList(
  narrativeDetail: NarrativeDetailDto | null,
  dataPoints: TimelineDataPoint[]
): { address: string; symbol: string }[] {
  return useMemo(() => {
    const addresses = narrativeDetail?.tokenAddresses ?? [];
    if (addresses.length === 0) return [];
    const lastPoint = dataPoints.length > 0 ? dataPoints[dataPoints.length - 1] : null;
    const symbolByAddress = new Map<string, string>();
    if (lastPoint) {
      lastPoint.tokenMarketCaps.forEach((t) => symbolByAddress.set(t.address, t.symbol));
    }
    return addresses.map((address) => ({
      address,
      symbol: symbolByAddress.get(address) ?? '-',
    }));
  }, [narrativeDetail?.tokenAddresses, dataPoints]);
}

const NarrativeDetailPage: React.FC<NarrativeDetailPageProps> = ({ narrativeId, onBack, onDeleted }) => {
  const { showToaster } = useToasterStore();
  const { setSelectedToken, openTokenDetailModal } = useUIStore();
  const [days, setDays] = useState<number>(7);
  const [topN, setTopN] = useState<number>(10);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<GetNarrativeStatisticsResponse | null>(null);
  const [narrativeDetail, setNarrativeDetail] = useState<NarrativeDetailDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newTokenAddress, setNewTokenAddress] = useState('');
  const [addingToken, setAddingToken] = useState(false);
  const [newStakeholder, setNewStakeholder] = useState<{
    twitterHandle: string;
    name: string;
    description: string;
  }>({ twitterHandle: '', name: '', description: '' });
  const [addingStakeholder, setAddingStakeholder] = useState(false);
  const [newEvent, setNewEvent] = useState<{ description: string; timestamp: string }>({
    description: '',
    timestamp: '',
  });
  const [addingEvent, setAddingEvent] = useState(false);
  const [addTokenModalOpen, setAddTokenModalOpen] = useState(false);
  const [addStakeholderModalOpen, setAddStakeholderModalOpen] = useState(false);
  const [addEventModalOpen, setAddEventModalOpen] = useState(false);
  const [isContentEditing, setIsContentEditing] = useState(false);
  const [contentDraft, setContentDraft] = useState('');
  const [isContentSaving, setIsContentSaving] = useState(false);
  const [contentHovered, setContentHovered] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleStartContentEdit = useCallback(() => {
    setContentDraft(narrativeDetail?.content ?? '');
    setIsContentEditing(true);
  }, [narrativeDetail?.content]);

  const handleCancelContentEdit = useCallback(() => {
    setIsContentEditing(false);
    setContentDraft('');
  }, []);

  const handleSaveContent = useCallback(async () => {
    if (isContentSaving || narrativeId == null) return;
    setIsContentSaving(true);
    try {
      const res = await feManagementSystemApi.updateNarrative(narrativeId, { content: contentDraft });
      setNarrativeDetail(res.data ?? null);
      setIsContentEditing(false);
      setContentDraft('');
      showToaster('Content updated.', 'success');
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err, 'Failed to update content.');
      showToaster(msg, 'error');
    } finally {
      setIsContentSaving(false);
    }
  }, [narrativeId, contentDraft, isContentSaving, showToaster]);

  const loadDetailData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNarrativeDetail(null);
    try {
      const statsRes = await feManagementSystemApi.getNarrativeStatistics(narrativeId, { days, topN });
      setStats(statsRes);
      try {
        const detailRes = await feManagementSystemApi.getNarrativeById(narrativeId);
        setNarrativeDetail(detailRes.data);
      } catch {
        setNarrativeDetail(null);
      }
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err, 'Failed to load narrative statistics.');
      setError(msg);
      setStats(null);
      setNarrativeDetail(null);
      showToaster(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [narrativeId, days, topN, showToaster]);

  useEffect(() => {
    loadDetailData();
  }, [loadDetailData]);

  const dataPoints = useMemo(
    () => stats?.data?.timeline?.dataPoints ?? [],
    [stats?.data?.timeline?.dataPoints]
  );
  const lastPoint = dataPoints.length > 0 ? dataPoints[dataPoints.length - 1] : null;
  const currentTotalMcap = lastPoint?.totalMarketCap ?? 0;
  const athMcap = useMemo(() => {
    if (dataPoints.length === 0) return 0;
    return Math.max(...dataPoints.map((p) => p.totalMarketCap));
  }, [dataPoints]);
  const tokenCount = lastPoint?.tokenCount ?? 0;
  const narrativeName = stats?.data?.name ?? '-';

  const tokenList = useNarrativeTokenList(narrativeDetail, dataPoints);

  const handleOpenTokenDetail = useCallback(
    (address: string, symbol: string) => {
      setSelectedToken({ tokenAddress: address, symbol: symbol || 'Unknown' });
      openTokenDetailModal();
    },
    [setSelectedToken, openTokenDetailModal]
  );

  const handleAddToken = useCallback(async (onSuccess?: () => void) => {
    const address = newTokenAddress.trim();
    if (!address) {
      showToaster('토큰 주소를 입력하세요.', 'info');
      return;
    }
    const current = narrativeDetail?.tokenAddresses ?? [];
    if (current.includes(address)) {
      showToaster('이미 추가된 토큰 주소입니다.', 'info');
      return;
    }
    setAddingToken(true);
    try {
      const res = await feManagementSystemApi.setNarrativeTokens(narrativeId, {
        tokenAddresses: [...current, address],
      });
      setNarrativeDetail(res.data);
      setNewTokenAddress('');
      showToaster('토큰이 추가되었습니다. Fetch Data로 통계를 다시 불러오세요.', 'success');
      onSuccess?.();
    } catch (err: unknown) {
      showToaster(getApiErrorMessage(err, 'Failed to add token.'), 'error');
    } finally {
      setAddingToken(false);
    }
  }, [narrativeId, newTokenAddress, narrativeDetail?.tokenAddresses, showToaster]);

  const handleAddStakeholder = useCallback(async (onSuccess?: () => void) => {
    const handle = newStakeholder.twitterHandle.trim();
    if (!handle) {
      showToaster('Twitter handle을 입력하세요.', 'info');
      return;
    }
    const current = narrativeDetail?.stakeholders ?? [];
    if (current.some((s) => s.twitterHandle.toLowerCase() === handle.toLowerCase())) {
      showToaster('이미 추가된 stakeholder입니다.', 'info');
      return;
    }
    setAddingStakeholder(true);
    try {
      const payload: NarrativeStakeholderDto = {
        twitterHandle: handle,
        ...(newStakeholder.name.trim() && { name: newStakeholder.name.trim() }),
        ...(newStakeholder.description.trim() && { description: newStakeholder.description.trim() }),
      };
      const res = await feManagementSystemApi.setNarrativeStakeholders(narrativeId, {
        stakeholders: [...current, payload],
      });
      setNarrativeDetail(res.data);
      setNewStakeholder({ twitterHandle: '', name: '', description: '' });
      showToaster('Stakeholder가 추가되었습니다.', 'success');
      onSuccess?.();
    } catch (err: unknown) {
      showToaster(getApiErrorMessage(err, 'Failed to add stakeholder.'), 'error');
    } finally {
      setAddingStakeholder(false);
    }
  }, [narrativeId, newStakeholder, narrativeDetail?.stakeholders, showToaster]);

  const handleAddEvent = useCallback(async (onSuccess?: () => void) => {
    const description = newEvent.description.trim();
    const timestamp = newEvent.timestamp.trim();
    if (!description || !timestamp) {
      showToaster('Description과 Timestamp를 입력하세요.', 'info');
      return;
    }
    const current = (narrativeDetail?.events ?? []) as NarrativeEventDto[];
    setAddingEvent(true);
    try {
      const payload: NarrativeEventDto = { description, timestamp };
      const res = await feManagementSystemApi.setNarrativeEvents(narrativeId, {
        events: [...current, payload],
      });
      setNarrativeDetail(res.data);
      setNewEvent({ description: '', timestamp: '' });
      showToaster('Event가 추가되었습니다.', 'success');
      onSuccess?.();
    } catch (err: unknown) {
      showToaster(getApiErrorMessage(err, 'Failed to add event.'), 'error');
    } finally {
      setAddingEvent(false);
    }
  }, [narrativeId, newEvent, narrativeDetail?.events, showToaster]);

  const handleDeleteNarrative = useCallback(async () => {
    if (!window.confirm(`"${narrativeDetail?.name ?? narrativeName}" 내러티브를 삭제할까요?`)) return;
    setIsDeleting(true);
    try {
      await feManagementSystemApi.deleteNarrative(narrativeId);
      showToaster('내러티브가 삭제되었습니다.', 'success');
      onDeleted?.();
      onBack();
    } catch (err: unknown) {
      showToaster(getApiErrorMessage(err, '내러티브 삭제에 실패했습니다.'), 'error');
    } finally {
      setIsDeleting(false);
    }
  }, [narrativeId, narrativeDetail?.name, narrativeName, onBack, onDeleted, showToaster]);

  return (
    <div className="h-screen bg-gray-900 text-white flex flex-col min-h-0">
      <Header title="AF Monitor" showPageControls={false} />
      <div className="flex-1 min-h-0 overflow-y-auto p-6">
        <div className="mb-4 flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-300 hover:text-white border border-gray-600 rounded-md hover:bg-gray-700"
          >
            ← 목록으로
          </button>
          <span className="text-gray-400">|</span>
          <span className="text-white font-medium">{narrativeName}</span>
          <button
            type="button"
            onClick={handleDeleteNarrative}
            disabled={isDeleting}
            className="ml-2 px-2 py-1 text-xs text-red-400 hover:text-red-300 border border-red-600/50 rounded hover:bg-red-900/30 disabled:opacity-50"
          >
            {isDeleting ? '삭제 중...' : '삭제'}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-4 mb-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-400 whitespace-nowrap">Days</label>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
            >
              {DAYS_OPTIONS.map((d) => (
                <option key={d} value={d}>{d} days</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-400 whitespace-nowrap">Top N Tokens</label>
            <select
              value={topN}
              onChange={(e) => setTopN(Number(e.target.value))}
              className="px-3 py-1.5 text-sm bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
            >
              {TOP_N_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={loadDetailData}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Fetch Data'}
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded bg-red-900/30 border border-red-700 text-red-200 text-sm">
            {error}
          </div>
        )}

        {stats && (
          <div className="mb-6 p-4 bg-gray-800/50 rounded-lg border border-gray-700 space-y-4">
            <div className="flex flex-wrap gap-6">
              <div>
                <span className="text-gray-400 text-sm">Narrative Name</span>
                <p className="text-white font-medium">{narrativeName}</p>
              </div>
              <div>
                <span className="text-gray-400 text-sm">Current Total MC</span>
                <p className="text-white font-medium">${formatNumberToLargestUnit(currentTotalMcap)}</p>
              </div>
              <div>
                <span className="text-gray-400 text-sm">ATH Market Cap</span>
                <p className="text-white font-medium">${formatNumberToLargestUnit(athMcap)}</p>
              </div>
              <div>
                <span className="text-gray-400 text-sm">Token Count</span>
                <p className="text-white font-medium">{tokenCount}</p>
              </div>
            </div>
            <div
              className="w-full border-t border-gray-700 pt-4"
              onMouseEnter={() => setContentHovered(true)}
              onMouseLeave={() => setContentHovered(false)}
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-gray-400 text-sm">Content</span>
                {!isContentEditing && contentHovered && (
                  <button
                    type="button"
                    onClick={handleStartContentEdit}
                    className="shrink-0 px-2 py-1 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-500"
                  >
                    편집
                  </button>
                )}
              </div>
              {isContentEditing ? (
                <>
                  <textarea
                    value={contentDraft}
                    onChange={(e) => setContentDraft(e.target.value)}
                    className="w-full min-h-[120px] px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white text-sm whitespace-pre-wrap break-words focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                    placeholder="Content (optional)"
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <button
                      type="button"
                      onClick={handleCancelContentEdit}
                      disabled={isContentSaving}
                      className="px-3 py-1.5 text-sm font-medium rounded bg-gray-600 text-white hover:bg-gray-500 disabled:opacity-50"
                    >
                      취소
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveContent}
                      disabled={isContentSaving}
                      className="px-3 py-1.5 text-sm font-medium rounded bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50"
                    >
                      {isContentSaving ? '저장 중...' : '저장'}
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-white text-sm whitespace-pre-wrap break-words">
                  {narrativeDetail?.content != null && narrativeDetail.content !== ''
                    ? narrativeDetail.content
                    : '\u00a0'}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Left: Tokens in this narrative */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700 flex flex-col min-w-0">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h3 className="text-sm font-semibold text-gray-300">Tokens in this narrative</h3>
              <button
                type="button"
                onClick={() => setAddTokenModalOpen(true)}
                className="shrink-0 px-2 py-1 text-xs font-medium rounded bg-emerald-600 text-white hover:bg-emerald-500"
              >
                + Add
              </button>
            </div>
            {tokenList.length > 0 ? (
              <div className="overflow-x-auto flex-1 min-h-0">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-gray-600">
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-400">Symbol</th>
                      <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-400">Address</th>
                      <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-400 w-14">Detail</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {tokenList.map(({ address, symbol }) => (
                      <tr key={address} className="hover:bg-gray-700/30">
                        <td className="px-2 py-1.5 text-white">{symbol}</td>
                        <td className="px-2 py-1.5 text-gray-400 font-mono break-all">{address}</td>
                        <td className="px-2 py-1.5 text-right">
                          <button
                            type="button"
                            onClick={() => handleOpenTokenDetail(address, symbol)}
                            className="px-2 py-1 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-500"
                          >
                            Detail
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No tokens yet.</p>
            )}
          </div>

          {/* Center: Stakeholders */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700 flex flex-col min-w-0">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h3 className="text-sm font-semibold text-gray-300">Stakeholders</h3>
              <button
                type="button"
                onClick={() => setAddStakeholderModalOpen(true)}
                className="shrink-0 px-2 py-1 text-xs font-medium rounded bg-emerald-600 text-white hover:bg-emerald-500"
              >
                + Add
              </button>
            </div>
            {(narrativeDetail?.stakeholders?.length ?? 0) > 0 ? (
              <ul className="divide-y divide-gray-700 text-sm overflow-y-auto flex-1 min-h-0">
                {(narrativeDetail?.stakeholders ?? []).map((s) => (
                  <li key={s.twitterHandle} className="py-2 first:pt-0">
                    <span className="font-medium text-white">@{s.twitterHandle}</span>
                    {s.name && <span className="text-gray-400 ml-1">· {s.name}</span>}
                    {s.description && (
                      <p className="text-gray-500 text-xs mt-0.5 break-words">{s.description}</p>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-sm">No stakeholders yet.</p>
            )}
          </div>

          {/* Right: Events */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700 flex flex-col min-w-0">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h3 className="text-sm font-semibold text-gray-300">Events</h3>
              <button
                type="button"
                onClick={() => setAddEventModalOpen(true)}
                className="shrink-0 px-2 py-1 text-xs font-medium rounded bg-emerald-600 text-white hover:bg-emerald-500"
              >
                + Add
              </button>
            </div>
            {((narrativeDetail?.events?.length) ?? 0) > 0 ? (
              <ul className="divide-y divide-gray-700 text-sm overflow-y-auto flex-1 min-h-0">
                {((narrativeDetail?.events ?? []) as NarrativeEventDto[]).map((ev, i) => (
                  <li key={`${ev.timestamp}-${i}`} className="py-2 first:pt-0">
                    <p className="text-white break-words">{ev.description}</p>
                    <p className="text-gray-500 text-xs mt-0.5">
                      {ev.timestamp ? new Date(ev.timestamp).toLocaleString() : '-'}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-sm">No events yet.</p>
            )}
          </div>
        </div>

        {stats && dataPoints.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <NarrativeTotalMcapChart dataPoints={dataPoints} />
            <NarrativeStackedMcapChart dataPoints={dataPoints} />
            <NarrativeIndividualMcapChart dataPoints={dataPoints} />
            <NarrativeTokenCountChart dataPoints={dataPoints} />
          </div>
        )}

        {stats && dataPoints.length === 0 && !loading && (
          <p className="text-gray-400 text-sm">No timeline data for this narrative.</p>
        )}

        {/* Add Token Modal */}
        {addTokenModalOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
            onClick={() => {
              setAddTokenModalOpen(false);
              setNewTokenAddress('');
            }}
          >
            <div
              className="bg-gray-800 rounded-lg p-6 w-full max-w-md shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-sm font-semibold text-gray-300 mb-4">Add token</h3>
              <input
                type="text"
                value={newTokenAddress}
                onChange={(e) => setNewTokenAddress(e.target.value)}
                placeholder="Token address"
                className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 mb-4"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setAddTokenModalOpen(false);
                    setNewTokenAddress('');
                  }}
                  className="px-3 py-1.5 text-sm text-gray-300 hover:text-white border border-gray-600 rounded hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleAddToken(() => setAddTokenModalOpen(false))}
                  disabled={addingToken}
                  className="px-3 py-1.5 text-sm font-medium rounded bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
                >
                  {addingToken ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Stakeholder Modal */}
        {addStakeholderModalOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
            onClick={() => {
              setAddStakeholderModalOpen(false);
              setNewStakeholder({ twitterHandle: '', name: '', description: '' });
            }}
          >
            <div
              className="bg-gray-800 rounded-lg p-6 w-full max-w-md shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-sm font-semibold text-gray-300 mb-4">Add stakeholder</h3>
              <div className="space-y-3 mb-4">
                <input
                  type="text"
                  value={newStakeholder.twitterHandle}
                  onChange={(e) => setNewStakeholder((p) => ({ ...p, twitterHandle: e.target.value }))}
                  placeholder="Twitter handle"
                  className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
                <input
                  type="text"
                  value={newStakeholder.name}
                  onChange={(e) => setNewStakeholder((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Name (optional)"
                  className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
                <input
                  type="text"
                  value={newStakeholder.description}
                  onChange={(e) => setNewStakeholder((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Description (optional)"
                  className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setAddStakeholderModalOpen(false);
                    setNewStakeholder({ twitterHandle: '', name: '', description: '' });
                  }}
                  className="px-3 py-1.5 text-sm text-gray-300 hover:text-white border border-gray-600 rounded hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleAddStakeholder(() => setAddStakeholderModalOpen(false))}
                  disabled={addingStakeholder}
                  className="px-3 py-1.5 text-sm font-medium rounded bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
                >
                  {addingStakeholder ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Event Modal */}
        {addEventModalOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
            onClick={() => {
              setAddEventModalOpen(false);
              setNewEvent({ description: '', timestamp: '' });
            }}
          >
            <div
              className="bg-gray-800 rounded-lg p-6 w-full max-w-md shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-sm font-semibold text-gray-300 mb-4">Add event</h3>
              <div className="space-y-3 mb-4">
                <input
                  type="text"
                  value={newEvent.description}
                  onChange={(e) => setNewEvent((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Event description"
                  className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
                <input
                  type="datetime-local"
                  value={newEvent.timestamp ? newEvent.timestamp.slice(0, 16) : ''}
                  onChange={(e) =>
                    setNewEvent((p) => ({
                      ...p,
                      timestamp: e.target.value ? new Date(e.target.value).toISOString() : '',
                    }))
                  }
                  className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:border-blue-500"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setAddEventModalOpen(false);
                    setNewEvent({ description: '', timestamp: '' });
                  }}
                  className="px-3 py-1.5 text-sm text-gray-300 hover:text-white border border-gray-600 rounded hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleAddEvent(() => setAddEventModalOpen(false))}
                  disabled={addingEvent}
                  className="px-3 py-1.5 text-sm font-medium rounded bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50"
                >
                  {addingEvent ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(NarrativeDetailPage);
